<script setup lang="ts">
import type { OutputBlockData, OutputData } from '@editorjs/editorjs';
import { vNavigateLinks } from '../';
import { splitHtml } from '~/shared/utils/splitHtml';
import ArticleParser from './parser/ArticleParser.vue';
import { resizeHtmlImages } from '~/shared/lib/resizeHtmlImages';

type ParsedContentOutputType = {
  type: 'json';
  data: OutputData;
};

type ParsedContentHtmlType = {
  type: 'html';
  data: string;
};

const props = defineProps<{
  text: string;
}>();

const parseContent = (
  content: string,
): ParsedContentHtmlType | ParsedContentOutputType => {
  try {
    const parsed = JSON.parse(content);
    return { type: 'json', data: parsed };
  } catch {
    return { type: 'html', data: resizeHtmlImages(content) };
  }
};

const preparedContent = parseContent(props.text);
const [firstPart, secondPart] =
  preparedContent.type === 'json'
    ? [
        preparedContent.data.blocks.slice(
          0,
          preparedContent.data.blocks.length / 2,
        ),
        preparedContent.data.blocks.slice(
          preparedContent.data.blocks.length / 2,
        ),
      ]
    : splitHtml(preparedContent.data);
</script>

<template>
  <div v-navigate-links :class="$style.item">
    <div v-if="preparedContent.type === 'html'" v-html="firstPart" />
    <ArticleParser v-else :blocks="firstPart as OutputBlockData[]" />
    <slot />
    <div v-if="preparedContent.type === 'html'" v-html="secondPart" />
    <ArticleParser v-else :blocks="secondPart as OutputBlockData[]" />
  </div>
</template>

<style module lang="scss">
.item {
  @include text_body1;
  color: var(--color-black-1);

  :global(a) {
    cursor: pointer;
    color: var(--color-red-1);
    text-decoration: none;
    background: linear-gradient(
      to bottom,
      var(--color-red-1) 50%,
      var(--color-red-1) 50%
    );
    background-repeat: repeat-x;
    background-position: 0 100%;
    background-size: 1px 1px;

    &:hover {
      color: var(--color-red-2);
    }
  }

  :global(p) {
    margin-top: 0;
    margin-bottom: var(--space-2x);
  }

  :global(p:last-child) {
    margin-bottom: 0;
  }

  :global(b) {
    @include text_body1_bold;
  }

  :global(h2) {
    @include text_h2;
    margin-top: var(--space-5x);
    margin-bottom: var(--space-3x);

    &:first-child {
      margin-top: 0;
    }
  }

  :global(h3) {
    @include text_h3;
    margin-top: var(--space-4x);
    margin-bottom: var(--space-2x);

    &:first-child {
      margin-top: 0;
    }
  }

  :global(h4) {
    @include text_h4;
    margin-top: var(--space-3x);
    margin-bottom: var(--space-2x);

    &:first-child {
      margin-top: 0;
    }
  }

  :global(> img) {
    display: block;
    border: 0;
    max-width: 100%;
    height: auto;
    margin-bottom: var(--space-3x);
    margin-top: var(--space-3x);
  }

  :global(> img + .source) {
    margin-top: -16px;
  }

  // TODO: Сейчас на сайте источник фото указан тегом <em /> и у него инлайновые стили
  // Надо будет какой-то отдельный класс под источник?
  :global(.source) {
    @include text_body2;
    color: var(--color-gray-text);
  }

  :global(.key-point) {
    background: var(--color-gray-bg);
    border: 1px solid var(--color-gray-line);
    padding: var(--space-2-5x) 30px;
    @include text_h3;
    font-size: 24px;
    color: var(--color-black-1);
    margin-bottom: var(--space-5x);
    margin-top: var(--space-3x);
  }

  :global(.editor-deep-link) {
    background: var(--color-red-3);
    border-bottom: 1px solid var(--color-red-2);
    border-radius: 40px;
    color: var(--color-red-2);
    padding-left: 10px;
    padding-right: 10px;
    text-decoration: none;
    cursor: pointer;
  }

  :global(ul) {
    margin: var(--space-3x) 0;
    padding-left: var(--space-3x);
  }
  :global(ul li) {
    margin-bottom: var(--space-1-5x);
    list-style: none;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      content: '';
      position: absolute;
      left: -20px;
      top: 9px;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: var(--color-black-1);
    }
  }

  :global(ol) {
    margin: var(--space-3x) 0;
    padding-left: var(--space-3x);
  }
  :global(ol li) {
    margin-bottom: var(--space-1-5x);
    &:last-child {
      margin-bottom: 0;
    }
  }
}

@media (min-width: 480px) and (max-width: 767px) {
  .item {
    :global(.key-point) {
      font-size: 20px;
    }
  }
}

@media (max-width: 767px) {
  .item {
    :global(h2) {
      @include text_h2_mobile;
    }
    :global(h3) {
      @include text_h3_mobile;
    }
    :global(h4) {
      @include text_h4_mobile;
    }
    :global(h5) {
      @include text_h5_mobile;
    }
  }
}

@media (max-width: 479px) {
  .item {
    :global(.key-point) {
      margin-left: -20px;
      margin-right: -20px;
      padding: var(--space-2-5x) var(--space-2x);
      @include text_h5;
      font-size: 20px;
    }
  }
}
</style>
